import { Descriptions, Tag } from 'antd';
import type { DescriptionsProps } from 'antd';
import { useMemo } from 'react';

import { useAdminPersonsController_findOne } from '@api-client/generated/AdminPersonsController/findOne';
import { Schemas } from '@api-client/generated/types';

import {
  getAccountingPlan,
  getCountryDataByCode,
  geyKycStatus,
} from '../helpers';

type CompanyDetailsProps = {
  data: Schemas.Incorporation;
};

export const CompanyDetails = ({ data }: CompanyDetailsProps) => {
  const { data: company } = useAdminPersonsController_findOne({
    params: {
      id: data.id,
      companyId: data.id,
    },
  });

  const kycStatus = useMemo(
    () =>
      geyKycStatus({
        isKycFailed: data.isKycFailed,
        isCheckComplete: data.isCheckComplete,
        isSubmittedToKyc: data.isSubmittedToKyc,
      }),
    [data]
  );

  const accountingPlan = useMemo(() => getAccountingPlan(data), [data]);

  const items: DescriptionsProps['items'] = useMemo(
    () => [
      {
        key: 'stage',
        label: 'Stage',
        children: data.group,
      },
      {
        key: 'name',
        label: 'Name(s)',
        children: data.companyNames.join(', '),
      },
      {
        key: 'companyType',
        label: 'Type',
        children: data.companyType || '-',
      },
      {
        key: 'incorporationType',
        label: 'Incorporation type',
        children: data.incorporationType,
      },
      {
        key: 'kycStatus',
        label: 'KYC status',
        children: (
          <Tag color={kycStatus.color} bordered={false}>
            {kycStatus.label}
          </Tag>
        ),
      },
      {
        key: 'accountingPlan',
        label: 'Selected plan',
        children: accountingPlan,
      },
      {
        key: 'paymentStatus',
        label: 'Payment status',
        children: (
          <Tag color={data.isPaid ? 'success' : 'warning'} bordered={false}>
            {data.isPaid ? 'Paid' : 'Unpaid'}
          </Tag>
        ),
      },
      {
        key: 'paymentMethod',
        label: 'Payment method',
        children: data.paymentMethod === 'card' ? 'Card' : 'Bank Transfer',
      },
      {
        key: 'companyActivity',
        label: 'Company activity',
        span: 3,
        children: data.companyActivity,
      },
      {
        key: 'services',
        label: 'Services',
        span: 3,
        children: (
          <ul>
            <li>
              Needs EasyBiz to handle their business licence:{' '}
              <strong>{data.needsBusinessPermit ? 'yes' : 'no'}</strong>
            </li>
            <li>
              Need help setting up a Capital Blocking Certificate and a bank
              account:{' '}
              <strong>{data.needsBankAssistance ? 'yes' : 'no'}</strong>
            </li>
            <li>
              Needs EasyBiz to provide an address:{' '}
              <strong>{!data.hasOwnRegisteredAddress ? 'yes' : 'no'}</strong>
            </li>
          </ul>
        ),
      },
      {
        key: 'registrationAddress',
        label: 'Registration address',
        span: 3,
        children: (
          <>
            {[
              company?.kycData?.address?.countryCode &&
                getCountryDataByCode(company?.kycData?.address?.countryCode),
              company?.kycData?.address?.city,
              company?.kycData?.address?.street,
              company?.kycData?.address?.houseNumber,
              company?.kycData?.address?.postalCode,
            ]
              .filter(Boolean)
              .join(', ')}
          </>
        ),
      },
      {
        key: 'businessInformation',
        label: 'Business information',
        span: 6,
        children: (
          <>
            <ul>
              <li>
                Employees amount:{' '}
                {company?.kycData?.businessInformation?.employeesAmount || '-'}
              </li>
              <li>
                Client types:{' '}
                {company?.kycData?.businessInformation?.clientTypes?.join(', ')}
              </li>
              <li>
                Purposes:{' '}
                {company?.kycData?.businessInformation?.purpose || '-'}
              </li>
            </ul>
          </>
        ),
      },
      {
        key: 'businessDetails',
        label: 'Business details',
        span: 6,
        children: (
          <Descriptions
            items={[
              {
                key: 'vat',
                label: 'VAT number (if applicable)',
                children:
                  company?.kycData?.businessInformation?.vatNumber || '-',
              },
              {
                key: 'taxId',
                label: 'TAX ID number',
                children:
                  company?.kycData?.businessInformation?.taxIdNumber || '-',
              },
              {
                key: 'businessLicense',
                label: 'Business license (if applicable)',
                children:
                  company?.kycData?.businessInformation?.businessLicense || '-',
              },
              {
                key: 'employeesAmount',
                label: 'Employees amount',
                children:
                  company?.kycData?.businessInformation?.employeesAmount || '-',
              },
              {
                key: 'bankName',
                label: 'Bank name',
                children:
                  company?.kycData?.businessInformation?.bankName || '-',
              },
            ]}
            colon={false}
            layout="vertical"
          />
        ),
      },
      {
        key: 'accountingRequest',
        label: 'Accounting request',
        span: 6,
        children: (
          <Descriptions
            items={[
              {
                key: 'VAT periodicity',
                label: 'VAT periodicity',
                children:
                  company?.kycData?.accountingInfo?.vatPeriodicity || '-',
              },
              {
                key: 'firstVatReturnDate',
                label: 'First VAT return date',
                children: company?.kycData?.accountingInfo?.firstVatReturnDate
                  ? `${company?.kycData?.accountingInfo?.firstVatReturnDate.month}.${company?.kycData?.accountingInfo?.firstVatReturnDate.year}`
                  : '-',
              },
              {
                key: 'firstTaxReturnDate',
                label: 'First TAX return date',
                children: company?.kycData?.accountingInfo?.firstTaxReturnDate
                  ? `${company?.kycData?.accountingInfo?.firstTaxReturnDate.month}.${company?.kycData?.accountingInfo?.firstTaxReturnDate.year}`
                  : '-',
              },
              {
                key: 'financialYears',
                label: 'Financial years',
                children:
                  company?.kycData?.accountingInfo?.yearsToProvide?.join(', '),
              },
            ]}
            colon={false}
            layout="vertical"
          />
        ),
      },
    ],
    [data, kycStatus, accountingPlan, company]
  );

  return <Descriptions layout="vertical" items={items} colon={false} />;
};
